// Shared catalog helpers for the homepage - the vertical registry plus the small pure functions
// that turn a global-search / trending row into a label, a deep link and a price string. Lives in
// its own module so App.js and SearchExperience.js both use exactly the same mapping (no drift
// between the hero search and the trending strip).

export const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

// `category` splits the homepage's "Businesses" grid from its "Other services" grid - gym
// membership isn't an item-rental business like the other four. Search / trending / lookups treat
// every entry the same.
export const verticals = [
  {
    name: 'Jewellery Rental',
    verticalKey: 'jewelry',
    tag: 'Luxury access',
    blurb: 'Premium pieces with insured protection and concierge-level support from us.',
    icon: '💎',
    url: process.env.REACT_APP_JEWELRY_URL || 'http://localhost:5173',
    category: 'rental',
  },
  {
    name: 'Car Rental',
    verticalKey: 'car_rental',
    tag: 'Smart mobility',
    blurb: 'Daily and long-stay vehicles with verified pickup and pricing controls.',
    icon: '🚗',
    url: process.env.REACT_APP_CAR_RENTAL_URL || 'http://localhost:5174',
    category: 'rental',
  },
  {
    name: 'Real Estate',
    verticalKey: 'real_estate',
    tag: 'Premium stays',
    blurb: 'Curated homes and apartments with verified listings and site-visit flows.',
    icon: '🏠',
    url: process.env.REACT_APP_REAL_ESTATE_URL || 'http://localhost:5176',
    category: 'rental',
  },
  {
    name: 'Hospitality',
    verticalKey: 'hospitality',
    tag: 'Travel experiences',
    blurb: 'Villas, resorts, and stay experiences for short trips and long getaways.',
    icon: '🏖️',
    url: process.env.REACT_APP_HOSPITALITY_URL || 'http://localhost:5177',
    category: 'rental',
  },
  {
    name: 'Gym Memberships',
    verticalKey: 'gym',
    tag: 'Fitness',
    blurb: 'Duration-based gym memberships with transparent pricing, promos and visit tracking.',
    icon: '🏋️',
    url: process.env.REACT_APP_GYM_URL || 'http://localhost:5178',
    category: 'service',
  },
];

// Backend vertical key -> the display label above.
export const VERTICAL_BY_KEY = {
  jewelry: 'Jewellery Rental',
  car_rental: 'Car Rental',
  real_estate: 'Real Estate',
  hospitality: 'Hospitality',
  gym: 'Gym Memberships',
};

export function verticalLabel(key) {
  return VERTICAL_BY_KEY[key] || key;
}

// A listing image path from the API -> a loadable URL. Bundled /images/dummy assets and absolute
// URLs pass through; an /uploads/ path is resolved against the API origin.
export function listingImage(image) {
  if (!image) return '';
  if (/^https?:\/\//.test(image)) return image;
  if (image.startsWith('/uploads/')) return `${API_BASE_URL}${image}`;
  return image;
}

export function trendingHref(deal) {
  const v = verticals.find((x) => x.name === VERTICAL_BY_KEY[deal.vertical]) || verticals[0];
  return deal._id ? `${v.url}/items/${deal._id}` : v.url;
}

// "View More Items" (ListingPreview.js) - that vertical app's OWN browse/listing page, not
// another single item. Every vertical's ItemList reads its filters straight off the URL
// (useBrowseFilters) and `category` is the one filter key shared by all five (see each
// vertical's own FILTERS array), so the item's own category carries over as the applied filter
// from the homepage preview - the visitor lands on more of what they were just looking at,
// instead of the unfiltered full catalog.
export function moreItemsHref(deal) {
  const v = verticals.find((x) => x.name === VERTICAL_BY_KEY[deal.vertical]) || verticals[0];
  return deal.category ? `${v.url}/?category=${encodeURIComponent(deal.category)}` : v.url;
}

// Keep the portal-navigation action descriptive instead of repeating a generic "Continue to"
// label. The destination is still the exact selected listing, not a broad category landing page.
export function exploreLabel(vertical) {
  return {
    car_rental: 'Explore more cabs',
    jewelry: 'Explore more jewellery',
    real_estate: 'Explore more properties',
    hospitality: 'Explore more stays',
    gym: 'Explore more memberships'
  }[vertical] || 'Explore more listings';
}

// A global-search result row -> that vertical app's real /items/:id detail page.
export function resultHref(item) {
  const v = verticals.find((x) => x.name === VERTICAL_BY_KEY[item.vertical]) || verticals[0];
  return `${v.url}/items/${item._id}`;
}

export function rupees(n, unit) {
  return `₹${Number(n || 0).toLocaleString('en-IN')}${unit ? `/${unit}` : ''}`;
}
